import { useArcTranslation } from 'hooks'
import { Card, Text, Stack, Group, Button, Box, Avatar, Divider } from 'components'
import { IconChevronDown, IconChartDonut3, IconUserPlus } from '@tabler/icons-react'
import { Menu } from '@mantine/core'
import { getInitials } from 'helpers'
import { IGroup } from 'interfaces'
import { useAddStudentsModal } from '../AddStudentsModal'

interface ArcGroupCardProps {
  group: IGroup
  membersLabel: string
}

export const ArcGroupCard = ({ group, membersLabel }: ArcGroupCardProps) => {
  const { t } = useArcTranslation()
  const { open: openAddStudentsModal } = useAddStudentsModal()

  const viewDiscURL = `/group_school_terms/${group.id}/group_school_term_mosaics?origin=dashboard`

  const totalStudents = group.students?.length ?? 0
  const hasStudents = totalStudents > 0

  return (
    <Card shadow='sm' withBorder radius='md' p={0} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box p='md'>
        <Group spacing='sm'>
          <Avatar size='lg' radius='xl' color='gray'>
            {getInitials(group.name)}
          </Avatar>
          <Text size='lg' weight={700}>
            {group.name}
          </Text>
        </Group>
      </Box>

      <Divider />

      {/* Content */}
      <Stack spacing='xs' p='md' style={{ flex: 1 }}>
        {hasStudents ? (
          <>
            <Text size='xs' color='dimmed' tt='uppercase'>
              {membersLabel} ({totalStudents})
            </Text>
            <Avatar.Group spacing='sm'>
              {group.students?.slice(0, 5).map((student, index) => (
                <Avatar key={index} size='md' radius='xl' src={student.img}>
                  {getInitials(student.fullName || student.name)}
                </Avatar>
              ))}
              {totalStudents > 5 && (
                <Avatar size='md' radius='xl' color='gray'>
                  +{totalStudents - 5}
                </Avatar>
              )}
            </Avatar.Group>
          </>
        ) : (
          <Text size='sm' color='dimmed'>
            {t('arc_dashboard.no_students')}
          </Text>
        )}
      </Stack>

      {/* Actions */}
      <Box px='md' pb='md'>
        <Menu shadow='md' width='target'>
          <Menu.Target>
            <Button
              fullWidth
              variant='outline'
              color='teal'
              radius='xl'
              size='sm'
              rightIcon={<IconChevronDown size={14} />}
            >
              {t('general_buttons_actions')}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item component='a' href={viewDiscURL} icon={<IconChartDonut3 size={16} />}>
              {t('new_teacher_dashboard.view_group_disc_button')}
            </Menu.Item>
            <Menu.Item icon={<IconUserPlus size={16} />} onClick={() => openAddStudentsModal(group.id)}>
              {t('new_teacher_dashboard.add_student_button')}
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Box>
    </Card>
  )
}
